import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import PoseCanvas from "@/components/capture/PoseCanvas";
import MultiPlayerPoseCanvas from "@/components/capture/MultiPlayerPoseCanvas";
import CameraGuide from "@/components/capture/CameraGuide";
import TrimSlider from "@/components/capture/TrimSlider";
import { Upload, Camera, Loader2, ArrowRight, User as UserIcon, Library, Users } from "lucide-react";
import { toast } from "sonner";

// PBCOACH.APP Motion Capture — pickleball-only port of VideoKinetix.
// Coach records or uploads a clip, MediaPipe Pose runs in-browser, strokes are
// classified (dink/drop/drive/volley/overhead/serve), ready position scored,
// then GPT analyzes form. Session is saved against an optional student.
// Quick-pick drill contexts. These phrases are parsed by the backend
// (`_parse_coach_context`) into a structured FOCUS, which decides which stroke
// types the analysis grades and which count toward the rating. Keep this list
// aligned with `_COACH_FOCUS_KEYWORDS` / `_COACH_STROKE_KEYWORDS` in
// backend/server.py — an unrecognised phrase falls back to "general capture"
// and the analysis will happily grade every stroke type it detects.
const SUGGESTIONS = [
  "Transition zone",
  "Third-shot drop",
  "Dink rally",
  "Drive return",
  "Serve technique",
  "Punch volley",
  "Overhead smash",
  "Footwork and split step",
];

export default function CoachCapture() {
  const [params] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState(params.get("student") || "");
  const [mode, setMode] = useState("live"); // live | upload
  const [subjectMode, setSubjectMode] = useState("singles"); // singles | doubles
  const [playMode, setPlayMode] = useState("drill"); // drill | game | match — affects rating cap
  const [videoSrc, setVideoSrc] = useState(null);
  const [notes, setNotes] = useState("");
  // Multi-view. Arriving from "Add another angle" carries `?group=<id>` so this
  // recording is saved as an extra camera angle on an existing analysis rather
  // than as a brand-new one. One clip per session is a hard invariant of the
  // clip route (a second upload replaces the first), so two angles are two
  // sessions that the detail page then re-groups.
  const groupId = params.get("group") || "";
  const isAddingAngle = !!groupId;
  const [viewLabel, setViewLabel] = useState(params.get("angle") || "");
  const [analyzing, setAnalyzing] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [trim, setTrim] = useState([0, 0]);
  const [scrubTime, setScrubTime] = useState(null);
  // Clip persistence: upload mode = the original File; live mode = MediaRecorder blob.
  const uploadFileRef = useRef(null);
  const recorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordedBlobRef = useRef(null);
  const fileRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data || [])).catch(() => {});
  }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Pre-flight format check — HEVC/H.265 in a .mov container often fails
    // to decode mid-playback in Safari and not at all in Chrome/Firefox.
    // We can't 100% detect the codec from a File object (the MIME type
    // lies), but we can warn loudly on the common .mov + iOS combo so the
    // user knows what to expect before the model loads.
    const name = (file.name || "").toLowerCase();
    const looksHEVC = name.endsWith(".mov") || file.type === "video/quicktime";
    if (looksHEVC) {
      toast.warning(
        "iPhone .mov clips are often HEVC — if it fails to load, re-export as MP4 (or set iOS Camera → Formats → Most Compatible).",
        { duration: 7000 }
      );
    }
    uploadFileRef.current = file;
    recordedBlobRef.current = null;
    setVideoSrc(URL.createObjectURL(file));
    setMode("upload");
  };

  // Live mode: as soon as PoseCanvas hands us the active MediaStream, spin up
  // a MediaRecorder so we capture the raw clip in parallel with pose inference.
  // We pick the best supported mime type per-browser; iOS Safari prefers mp4,
  // Chrome/Firefox prefer webm.
  //
  // In upload mode, PoseCanvas can also surface a captureStream (label
  // "upload-trim") so we record the trimmed analysis window as a saveable
  // clip — but ONLY if the user actually trimmed the source. For a full,
  // un-trimmed source the original file is already perfect and re-encoding
  // would waste CPU + bandwidth.
  const handleLiveStream = (stream, sourceLabel) => {
    if (!stream) return;
    if (sourceLabel === "upload-trim") {
      const dur = videoDuration;
      const isTrimmed = (trim[0] > 0.5) || (dur > 0 && trim[1] < dur - 0.5);
      if (!isTrimmed) return;
    }
    try {
      const candidates = [
        "video/mp4;codecs=avc1",
        "video/mp4",
        "video/webm;codecs=h264",
        "video/webm;codecs=vp9",
        "video/webm;codecs=vp8",
        "video/webm",
      ];
      let mime = "";
      for (const m of candidates) {
        if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) {
          mime = m; break;
        }
      }
      const opts = mime ? { mimeType: mime } : undefined;
      const rec = new MediaRecorder(stream, opts);
      recordedChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data); };
      rec.onstop = () => {
        if (recordedChunksRef.current.length > 0) {
          recordedBlobRef.current = new Blob(recordedChunksRef.current, { type: mime || "video/webm" });
        }
      };
      rec.start(1000); // flush a chunk every 1s so we don't lose data on crashes
      recorderRef.current = rec;
    } catch (e) {
      // MediaRecorder unsupported (very old browsers) — analysis still works, just no clip saved.
      console.warn("MediaRecorder unavailable; clip will not be saved", e);
    }
  };

  // Upload the recorded/picked clip to the just-created session. Failures here
  // do NOT abort the flow — the session was saved; the clip is just a bonus.
  //
  // Priority for which blob to upload:
  //   1. Trimmed re-recording (upload mode + user trimmed → MediaRecorder fired)
  //   2. Original uploaded file (upload mode, no trim)
  //   3. Live MediaRecorder blob (live mode)
  const uploadClip = async (sessionId) => {
    let blob = null;
    let filename = "clip.webm";
    if (mode === "upload" && recordedBlobRef.current) {
      blob = recordedBlobRef.current;
      const m = (blob.type || "video/webm").toLowerCase();
      filename = m.includes("mp4") ? "trimmed.mp4" : "trimmed.webm";
    } else if (mode === "upload" && uploadFileRef.current) {
      blob = uploadFileRef.current;
      filename = uploadFileRef.current.name || "clip.mp4";
    } else if (mode === "live" && recordedBlobRef.current) {
      blob = recordedBlobRef.current;
      const m = (blob.type || "video/webm").toLowerCase();
      filename = m.includes("mp4") ? "clip.mp4" : "clip.webm";
    }
    if (!blob) return;
    try {
      const fd = new FormData();
      fd.append("file", blob, filename);
      await api.post(`/capture/sessions/${sessionId}/clip`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (e) {
      toast.message("Session saved, but the clip upload failed — analysis is intact.");
    }
  };

  // PoseCanvas or MultiPlayerPoseCanvas hands us a summary once Stop is hit.
  // Multi-player payload comes in as { players: [{slot, color, ...}] } and we
  // forward it intact — backend AI handles both shapes.
  const handleStop = async (summary) => {
    setAnalyzing(true);
    // Finalize the live recording (if any) so the blob is ready by the time
    // we POST the clip below.
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
      // Give the ondataavailable/onstop handlers a tick to flush.
      await new Promise((r) => setTimeout(r, 250));
    }
    try {
      const isMulti = !!summary?.players?.length;
      // A run that produced fewer than two pose frames analysed nothing — the
      // playhead never played through the window. Surface that instead of
      // saving an empty "0s duration" session onto the roster and the reports.
      const framesProcessed = isMulti
        ? Math.max(0, ...summary.players.map((p) => p.frames_processed || 0))
        : (summary?.frames_processed || 0);
      if (framesProcessed < 2) {
        toast.error(
          "Nothing was analysed — the video didn't play through the trim window. Check the clip plays, drag the trim handles around the part you want, then start again.",
          { duration: 8000 }
        );
        setAnalyzing(false);
        return;
      }
      // Best-effort duration: multi-player summary doesn't carry a top-level
      // duration_seconds, so fall back to the longest player track.
      const duration_s = isMulti
        ? Math.max(0, ...summary.players.map((p) => p.duration_seconds || 0))
        : (summary?.duration_seconds || 0);
      const payload = {
        student_id: studentId || null,
        mode,
        play_mode: playMode,
        notes: notes.trim() || null,
        duration_seconds: duration_s,
        pose_summary: { ...summary, subject_mode: isMulti ? "doubles" : "singles" },
        // null session_group_id => the backend mints a new analysis.
        session_group_id: groupId || null,
        view_label: viewLabel.trim() || null,
      };
      const { data } = await api.post("/capture/sessions", payload);
      await uploadClip(data.id);
      // Tell the coach WHY a capture came back empty, instead of navigating to a
      // blank report. Pose tracking and stroke detection are separate stages: a
      // session can track the athlete perfectly and still hold zero strokes, and
      // "0 reps" with no explanation reads as a broken app.
      const repsTotal = isMulti
        ? summary.players.reduce((n, p) => n + (p.analysis?.rep_count || 0), 0)
        : (summary?.rep_count || 0);
      if (repsTotal === 0) {
        const ss = (isMulti ? summary.players[0]?.analysis?.signal_stats : summary?.signal_stats) || null;
        const detail =
          ss && ss.max != null && ss.min_prominence != null
            ? ` Peak wrist motion was ${ss.max} vs the ${ss.min_prominence} needed.`
            : "";
        toast.warning(
          `Session + clip saved, but no strokes were detected.${detail} Film closer so the athlete fills more of the frame, and trim around actual swings rather than waiting time.`,
          { duration: 10000 }
        );
      } else {
        toast.success("Session + clip saved");
      }
      nav(`/coach/capture/${data.id}`, { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    }
    setAnalyzing(false);
  };

  const onMetadataFromVideo = (d) => {
    if (Number.isFinite(d) && d > 0) {
      setVideoDuration(d);
      setTrim([0, d]);
    }
  };

  return (
    <div className="space-y-8" data-testid="coach-capture-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Motion Capture</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-2">Capture a session</h1>
          <p className="text-sm text-[#A3A3A3] mt-3 max-w-2xl">
            Use your phone camera or upload a clip. Strokes are classified in your browser — nothing leaves the device until you save.
          </p>
        </div>
        <Link
          to="/coach/capture"
          className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-2 mt-2"
          data-testid="capture-library-link"
        >
          <Library size={14} /> Past sessions
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          {/* Mode toggle */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex border border-white/15">
              <button
                onClick={() => { setMode("live"); setVideoSrc(null); }}
                className={`px-4 py-2 text-xs uppercase tracking-widest font-bold flex items-center gap-2 ${mode === "live" ? "bg-[#38BDF8] text-black" : "text-white/80 hover:text-white"}`}
                data-testid="capture-mode-live"
              >
                <Camera size={12} /> Live
              </button>
              <button
                onClick={() => { setMode("upload"); }}
                className={`px-4 py-2 text-xs uppercase tracking-widest font-bold flex items-center gap-2 ${mode === "upload" ? "bg-[#38BDF8] text-black" : "text-white/80 hover:text-white"}`}
                data-testid="capture-mode-upload"
              >
                <Upload size={12} /> Upload
              </button>
            </div>

            {/* Singles vs Doubles (multi-player) capture toggle. Forces a
                full remount of the canvas via the `key` prop below so the
                pose model + slot state both reset cleanly. */}
            <div className="inline-flex border border-white/15">
              <button
                onClick={() => setSubjectMode("singles")}
                className={`px-4 py-2 text-xs uppercase tracking-widest font-bold flex items-center gap-2 ${subjectMode === "singles" ? "bg-[#38BDF8] text-black" : "text-white/80 hover:text-white"}`}
                data-testid="capture-subject-singles"
                disabled={analyzing}
              >
                <UserIcon size={12} /> Singles
              </button>
              <button
                onClick={() => setSubjectMode("doubles")}
                className={`px-4 py-2 text-xs uppercase tracking-widest font-bold flex items-center gap-2 ${subjectMode === "doubles" ? "bg-[#38BDF8] text-black" : "text-white/80 hover:text-white"}`}
                data-testid="capture-subject-doubles"
                disabled={analyzing}
              >
                <Users size={12} /> Doubles · up to 4
              </button>
            </div>

            {/* Drill / Game / Match — tells the rating formula whether to
                treat this as controlled-pace drill footage (conservative
                caps) or real-pace game footage (slightly less conservative,
                rating can reach 5.0 instead of 4.5). */}
            <div className="inline-flex border border-white/15" data-testid="play-mode-toggle">
              <button
                onClick={() => setPlayMode("drill")}
                className={`px-3 py-2 text-xs uppercase tracking-widest font-bold ${playMode === "drill" ? "bg-[#38BDF8] text-black" : "text-white/80 hover:text-white"}`}
                data-testid="capture-play-drill"
                disabled={analyzing}
                title="Controlled-pace drill — rating capped at 4.5 (no tactical play visible)"
              >
                Drill
              </button>
              <button
                onClick={() => setPlayMode("game")}
                className={`px-3 py-2 text-xs uppercase tracking-widest font-bold ${playMode === "game" ? "bg-[#38BDF8] text-black" : "text-white/80 hover:text-white"}`}
                data-testid="capture-play-game"
                disabled={analyzing}
                title="Game-pace play / scrimmage — rating can reach 5.0"
              >
                Game
              </button>
              <button
                onClick={() => setPlayMode("match")}
                className={`px-3 py-2 text-xs uppercase tracking-widest font-bold ${playMode === "match" ? "bg-[#38BDF8] text-black" : "text-white/80 hover:text-white"}`}
                data-testid="capture-play-match"
                disabled={analyzing}
                title="Real competitive match — rating can reach 5.0"
              >
                Match
              </button>
            </div>
          </div>

          {subjectMode === "doubles" && (
            <p className="text-[11px] text-[#A3A3A3] -mt-1 max-w-2xl" data-testid="doubles-helper">
              Match capture: tap up to 4 players to lock skeletons onto each, then start. Each player gets their own breakdown.
            </p>
          )}

          {mode === "upload" && !videoSrc && (
            <div className="card-surface p-8 text-center" data-testid="capture-upload-empty">
              <Upload size={20} className="mx-auto text-[#38BDF8] mb-3" />
              <p className="text-sm text-[#A3A3A3] mb-3">Drop a .mov / .mp4 clip — best results are clips under 90 seconds, framed so the whole athlete is visible.</p>
              <button
                onClick={() => fileRef.current?.click()}
                className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-2"
                data-testid="capture-upload-button"
              >Choose file</button>
              <input
                ref={fileRef} type="file" accept="video/*"
                onChange={onFile}
                className="hidden"
                data-testid="capture-upload-input"
              />
            </div>
          )}

          {mode === "live" && !videoSrc && subjectMode === "singles" && (
            <CameraGuide sport="pickleball" data-testid="camera-guide" />
          )}

          {(mode === "live" || videoSrc) && (
            <div data-testid="pose-canvas-wrapper">
              {subjectMode === "doubles" ? (
                <MultiPlayerPoseCanvas
                  key={`mp-${mode}-${videoSrc || "live"}`}
                  sport="pickleball"
                  mode={mode}
                  videoSrc={videoSrc}
                  maxPlayers={4}
                  trimStart={trim[0]}
                  trimEnd={trim[1] || null}
                  scrubTime={scrubTime}
                  onStop={handleStop}
                  onReady={(d) => onMetadataFromVideo(d?.duration)}
                  onStream={handleLiveStream}
                />
              ) : (
                <PoseCanvas
                  key={`sp-${mode}-${videoSrc || "live"}`}
                  sport="pickleball"
                  mode={mode}
                  videoSrc={videoSrc}
                  trimStart={trim[0]}
                  trimEnd={trim[1] || null}
                  scrubTime={scrubTime}
                  onStop={handleStop}
                  onReady={(d) => onMetadataFromVideo(d?.duration)}
                  onStream={handleLiveStream}
                />
              )}
              {mode === "upload" && videoDuration > 0 && (
                <div className="mt-3 border-t border-white/10 pt-3" data-testid="trim-slider-wrap">
                  <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold mb-2">
                    Trim — only frames between the handles will be analyzed
                  </div>
                  <TrimSlider
                    duration={videoDuration}
                    value={trim}
                    onChange={setTrim}
                    onScrubTo={(t) => setScrubTime(t)}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="card-surface p-5 space-y-4 h-fit">
          {isAddingAngle && (
            <div
              className="rounded border border-[#38BDF8]/40 bg-[#38BDF8]/5 p-3 space-y-2"
              data-testid="add-angle-banner"
            >
              <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold inline-flex items-center gap-1.5">
                <Camera size={11} /> Adding a camera angle
              </div>
              <p className="text-[11px] text-white/70 leading-relaxed">
                This recording is saved as an extra angle on the existing analysis, so the
                student still sees one report. Film this camera on its own — don&apos;t try to
                fit both angles in frame.
              </p>
              <button
                type="button"
                onClick={() => nav("/coach/capture")}
                className="text-[10px] uppercase tracking-widest text-white/60 hover:text-white font-bold"
                data-testid="cancel-add-angle"
              >
                Cancel — start a new analysis instead
              </button>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">
              Camera angle {isAddingAngle ? "" : "(optional)"}
            </div>
            <select
              value={viewLabel}
              onChange={(e) => setViewLabel(e.target.value)}
              className="w-full bg-transparent border-b border-white/20 py-2 outline-none text-sm mt-1"
              data-testid="capture-view-label"
            >
              <option value="" className="bg-[#141414]">Not specified</option>
              <option value="net" className="bg-[#141414]">Net-side (paddle view)</option>
              <option value="baseline" className="bg-[#141414]">Behind baseline (rotation view)</option>
              <option value="side" className="bg-[#141414]">Dead side-on</option>
            </select>
            <p className="text-[10px] text-[#A3A3A3] mt-1.5 leading-relaxed">
              Label it now — the report uses it to say which angle each metric should be
              read from.
            </p>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Student (optional)</div>
            <select
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              disabled={isAddingAngle}
              className="w-full bg-transparent border-b border-white/20 py-2 outline-none text-sm mt-1 disabled:opacity-50"
              data-testid="capture-student-select"
            >
              <option value="" className="bg-[#141414]">No student — generic capture</option>
              {students.map((s) => (
                <option key={s.user_id} value={s.user_id} className="bg-[#141414]">
                  {s.name}{s.skill_level ? ` · DUPR ${s.skill_level}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Drill / context</div>
            <input
              type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Third-shot drop reps"
              className="w-full bg-transparent border-b border-white/20 py-2 outline-none text-sm mt-1"
              data-testid="capture-notes-input"
            />
            <div className="flex flex-wrap gap-1.5 mt-3">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setNotes(s)}
                  className="text-[10px] px-2 py-1 border border-white/10 text-[#A3A3A3] hover:border-[#38BDF8] hover:text-[#38BDF8]"
                  data-testid={`capture-suggestion-${s.slice(0, 10).replace(/\s+/g, "-").toLowerCase()}`}
                >{s}</button>
              ))}
            </div>
          </div>

          {analyzing && (
            <div className="border border-[#38BDF8]/40 bg-[#38BDF8]/5 p-3 text-xs flex items-center gap-2">
              <Loader2 size={14} className="animate-spin text-[#38BDF8]" />
              <span>Saving session + running form analysis…</span>
            </div>
          )}

          <p className="text-[11px] text-[#A3A3A3] leading-relaxed">
            Everything runs locally on your device. The clip is never uploaded — only the per-stroke metrics get saved to your roster.
          </p>
        </aside>
      </div>
    </div>
  );
}
